import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, ChevronRight, User } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/api';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';
import { matchPlateQuery } from '@/lib/plateMatch';

// PR-DROPDOWN-SCOPE MAY 12 — driver picker. Mirrors VehiclePicker: grouped by
// project, search, collapsible non-preferred group. Display format is
// "Full Name (PLATE)" so the operator can recognise a driver by the truck
// they're currently on. If a vehicle is selected and we know that vehicle's
// current driver, that driver bubbles to the top with a "RECOMMENDED" chip.

export interface DriverOption {
  id: string;
  code: string;
  fullNameEn: string | null;
  projectId: string | null;
  /** Any project's code. Was limited to two, which hid every other side. */
  projectCode: string | null;
  currentVehicle: { id: string; code: string | null; plateEn: string | null } | null;
}

type Scope = 'neom' | 'riyadh' | 'all';

interface Props {
  value: string | null;
  onChange: (id: string | null, driver: DriverOption | null) => void;
  preferProjectScope?: Scope;
  // Highlight this driver as RECOMMENDED. Use when the user has picked a
  // vehicle whose current driver is known.
  preferredDriverId?: string | null;
  placeholder?: string;
  className?: string;
}

interface RawDriver {
  id: string;
  code: string;
  fullNameEn: string | null;
  projectId: string | null;
  currentVehicles?: Array<{ id: string; code: string | null; plateEn: string | null }>;
}

/** Project id -> code and name, for every project. Cached after first load. */
const projectCache = new Map<string, { code: string; name: string }>();

export function DriverPicker({
  value, onChange,
  preferProjectScope = 'all',
  preferredDriverId,
  placeholder = 'Pick a driver…',
  className,
}: Props) {
  const { project } = useCurrentProject();
  const [items, setItems] = useState<DriverOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Which non-preferred groups are open, by key. One shared flag opened them all.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => { setOpenGroups({}); }, [preferProjectScope]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (projectCache.size === 0) {
          const r = await apiClient.get<{ items: Array<{ id: string; code: string }> }>(
            '/api/v1/projects',
          );
          for (const p of (r.data.items ?? []) as Array<{ id: string; code: string; name_en?: string; nameEn?: string }>) {
            // Every project, not a hand-written two: a side left out of this
            // list had its rows silently filed under OTHER.
            projectCache.set(p.id, { code: p.code, name: p.name_en ?? p.nameEn ?? p.code });
          }
        }
        const r = await apiClient.get<{ items?: RawDriver[] } | RawDriver[]>(
          '/api/v1/drivers?status=active&pageSize=500&projectScope=all',
        );
        const raw: RawDriver[] = Array.isArray(r.data) ? r.data : (r.data.items ?? []);
        const normalized: DriverOption[] = raw.map((d) => ({
          id: d.id,
          code: d.code,
          fullNameEn: d.fullNameEn,
          projectId: d.projectId,
          projectCode: d.projectId ? (projectCache.get(d.projectId)?.code ?? null) : null,
          currentVehicle: (d.currentVehicles && d.currentVehicles[0]) ?? null,
        }));
        if (!cancelled) setItems(normalized);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return items;
    const qLower = q.toLowerCase();
    // PR-MAY14-SMART-PLATE-SEARCH — the picker should find a driver by
    // their truck's plate even when the operator types digits-only or
    // letters-only or the swapped half-order.
    return items.filter((d) =>
      (d.fullNameEn ?? '').toLowerCase().includes(qLower)
      || d.code.toLowerCase().includes(qLower)
      || matchPlateQuery(q, d.currentVehicle?.plateEn));
  }, [items, query]);

  const preferred = items.find((d) => d.id === preferredDriverId) ?? null;

  const groups = useMemo(() => {
    // Built from the rows, not from a fixed list: every project gets its own
    // group and only genuinely unfiled drivers fall through to OTHER. All 33
    // dumper drivers used to land there.
    const byProject = new Map<string, DriverOption[]>();
    const other: DriverOption[] = [];
    for (const d of filtered) {
      // Don't double-render preferred — it shows in its own section.
      if (preferred && d.id === preferred.id) continue;
      if (d.projectCode) {
        const rows = byProject.get(d.projectCode);
        if (rows) rows.push(d); else byProject.set(d.projectCode, [d]);
      } else other.push(d);
    }
    const byName = (a: DriverOption, b: DriverOption) =>
      (a.fullNameEn ?? a.code).localeCompare(b.fullNameEn ?? b.code);
    byProject.forEach((rows) => rows.sort(byName));
    other.sort(byName);
    return { byProject, other };
  }, [filtered, preferred]);

  const selected = items.find((d) => d.id === value) ?? null;

  const orderedSections: Array<{
    key: string;
    label: string;
    rows: DriverOption[];
    primary: boolean;
  }> = (() => {
    const wanted = preferProjectScope === 'neom' ? 'PRJ-NEOM'
      : preferProjectScope === 'riyadh' ? 'PRJ-RIYADH' : null;
    // The project's own name, from the same cache the rows were tagged with.
    const labelFor = (code: string) => {
      for (const p of projectCache.values()) if (p.code === code) return p.name.toUpperCase();
      return code;
    };
    const sections = [...groups.byProject.entries()].map(([code, rows]) => ({
      key: code, label: labelFor(code), rows,
      primary: code === (wanted ?? project?.code ?? null),
    }));
    // Preferred first, then alphabetical, so the order is stable between opens.
    sections.sort((a, b) =>
      (b.primary ? 1 : 0) - (a.primary ? 1 : 0) || a.label.localeCompare(b.label));
    // Last: not a project, just the drivers nobody has filed.
    sections.push({ key: 'other', label: 'OTHER', rows: groups.other, primary: false });
    return sections;
  })();

  function pick(d: DriverOption) {
    onChange(d.id, d);
    setOpen(false);
    setQuery('');
  }

  function row(d: DriverOption, opts?: { recommended?: boolean }) {
    return (
      <button
        key={d.id}
        type="button"
        onClick={() => pick(d)}
        className={'w-full text-left px-3 py-2 hover:bg-muted/40 text-sm flex items-center justify-between '
          + (d.id === value ? 'bg-muted/30' : '')}
      >
        <span className="flex items-center gap-2 truncate">
          <span>{d.fullNameEn ?? d.code}</span>
          {d.currentVehicle?.plateEn && (
            <span className="text-xs text-muted-foreground">({d.currentVehicle.plateEn})</span>
          )}
        </span>
        {opts?.recommended && (
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30">
            Recommended
          </span>
        )}
      </button>
    );
  }

  return (
    <div className={'relative ' + (className ?? '')}>
      <button
        type="button"
        // Opening always starts from a clean list. The query used to
        // survive a close, so reopening showed the remains of an
        // earlier search - a single stray character hiding most of
        // the fleet behind a box that looks empty.
        onClick={() => { if (!open) setQuery(''); setOpen((s) => !s); }}
        className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-base text-left"
      >
        <span className={selected ? 'inline-flex items-center gap-2' : 'text-muted-foreground'}>
          {selected ? (
            <>
              <User className="h-4 w-4 text-muted-foreground" />
              {selected.fullNameEn ?? selected.code}
              {selected.currentVehicle?.plateEn && (
                <span className="text-xs text-muted-foreground">
                  ({selected.currentVehicle.plateEn})
                </span>
              )}
            </>
          ) : placeholder}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-[440px] flex flex-col">
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, plate, or code…"
              className="h-8 border-0 shadow-none focus-visible:ring-0 px-1"
            />
            {query && (
              <>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {filtered.length} of {items.length}
                </span>
                <button type="button" onClick={() => setQuery('')}
                  className="text-xs text-muted-foreground hover:text-foreground">Clear search</button>
              </>
            )}
            {value && (
              <button type="button" onClick={() => { onChange(null, null); setOpen(false); }}
                className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : (filtered.length === 0 && !preferred) ? (
              <div className="p-3 text-sm text-muted-foreground">No drivers match.</div>
            ) : (
              <>
                {preferred && (
                  <div>
                    <div className="px-3 py-1.5 bg-emerald-500/5 text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 border-b border-border">
                      Recommended for picked vehicle
                    </div>
                    {row(preferred, { recommended: true })}
                  </div>
                )}
                {orderedSections.map((section) => {
                  if (section.rows.length === 0) return null;
                  const leads = (preferProjectScope === 'neom' ? 'PRJ-NEOM'
                    : preferProjectScope === 'riyadh' ? 'PRJ-RIYADH' : null) ?? project?.code ?? null;
                  const expandedDefault = section.primary || query.length > 0 || !leads;
                  const isOpen = expandedDefault || !!openGroups[section.key];
                  return (
                    <div key={section.key}>
                      <button
                        type="button"
                        onClick={() => section.primary ? null
                          : setOpenGroups((g) => ({ ...g, [section.key]: !g[section.key] }))}
                        className="w-full px-3 py-1.5 bg-muted/30 flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground border-y border-border"
                      >
                        <span>{section.label} ({section.rows.length})</span>
                        {!section.primary && (
                          <ChevronRight className={'h-3 w-3 transition-transform ' + (isOpen ? 'rotate-90' : '')} />
                        )}
                      </button>
                      {isOpen && section.rows.map((d) => row(d))}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
